import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Shield, ShieldSlash, CurrencyDollar,
  Package, ArrowsLeftRight, CaretLeft, CaretRight, Crown, Prohibit, Trash
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PlatformStats } from '../components/admin/PlatformStats';
import { UserFilters } from '../components/admin/UserFilters';
import { ReconcilePanel } from '../components/admin/ReconcilePanel';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformStats, setPlatformStats] = useState(null);
  const [balanceDialog, setBalanceDialog] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceReason, setBalanceReason] = useState('');
  const [detailDialog, setDetailDialog] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [reconcileData, setReconcileData] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);

  const searchRef = useRef('');
  const updateSearch = (v) => { searchRef.current = v; setSearch(v); };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (searchRef.current) params.search = searchRef.current;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await adminAPI.getUsers(params);
      setUsers(data.users);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  }, [page, roleFilter, statusFilter]);

  const loadStats = useCallback(async () => {
    try { const { data } = await adminAPI.getPlatformStats(); setPlatformStats(data); } catch (err) { console.error('Failed to load platform stats:', err); }
  }, []);

  useEffect(() => { loadUsers(); loadStats(); }, [loadUsers, loadStats]);

  const handleBan = async (userId, name) => {
    if (!window.confirm(`Ban ${name}? They won't be able to log in.`)) return;
    try { await adminAPI.banUser(userId); toast.success(`${name} has been banned`); loadUsers(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleUnban = async (userId, name) => {
    try { await adminAPI.unbanUser(userId); toast.success(`${name} has been unbanned`); loadUsers(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDelete = async (userId, name, email) => {
    if (!window.confirm(`PERMANENTLY DELETE ${name} (${email})?\n\nThis will remove their account, products, transactions, and all associated data. This action CANNOT be undone.`)) return;
    if (!window.confirm(`Are you absolutely sure? Type confirms this is irreversible.`)) return;
    try { await adminAPI.deleteUser(userId); toast.success(`${name} has been permanently deleted`); loadUsers(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleRoleChange = async (userId, name, newRole) => {
    if (!window.confirm(`Change ${name}'s role to ${newRole}?`)) return;
    try { await adminAPI.changeRole(userId, newRole); toast.success(`${name} is now ${newRole}`); loadUsers(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleBalanceAdjust = async () => {
    if (!balanceDialog) return;
    const amount = parseFloat(balanceAmount);
    if (isNaN(amount) || amount === 0) { toast.error('Enter a valid amount'); return; }
    try {
      const { data } = await adminAPI.adjustBalance(balanceDialog.user_id, amount, balanceReason || 'Admin adjustment');
      toast.success(data.message);
      setBalanceDialog(null); setBalanceAmount(''); setBalanceReason('');
      loadUsers();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleReconcilePreview = async () => {
    setReconcileLoading(true);
    try { const { data } = await adminAPI.reconcilePreview(); setReconcileData(data); setShowReconcile(true); } catch (err) { toast.error(formatApiError(err)); }
    finally { setReconcileLoading(false); }
  };

  const handleReconcileApply = async () => {
    if (!window.confirm(`Apply balance reconciliation to ${reconcileData?.accounts_needing_adjustment} account(s)?`)) return;
    setReconcileLoading(true);
    try { const { data } = await adminAPI.reconcileApply(); toast.success(data.message); setShowReconcile(false); setReconcileData(null); loadUsers(); } catch (err) { toast.error(formatApiError(err)); }
    finally { setReconcileLoading(false); }
  };

  const openUserDetail = async (userId) => {
    try { const { data } = await adminAPI.getUserDetail(userId); setDetailData(data); setDetailDialog(true); } catch (err) { toast.error(formatApiError(err)); }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <Card className="bg-[#1A1A1A] border-[#333333] max-w-md"><CardContent className="p-8 text-center"><p className="text-white text-lg font-bold mb-2">Access Denied</p><p className="text-[#999999]">Admin privileges required.</p></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]" data-testid="admin-users-page">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard"><img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" /></Link>
            <span className="text-[#666666] hidden sm:inline">/</span>
            <span className="text-white font-semibold text-sm hidden sm:inline">User Management</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Link to="/admin/fees"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-xs sm:text-sm">Fees</Button></Link>
            <Link to="/admin/analytics"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-xs sm:text-sm">Analytics</Button></Link>
            <Link to="/dashboard"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-xs sm:text-sm"><ArrowLeft className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Dashboard</span></Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <PlatformStats stats={platformStats} formatCurrency={formatCurrency} />
        <UserFilters search={search} setSearch={updateSearch} roleFilter={roleFilter} setRoleFilter={setRoleFilter} statusFilter={statusFilter} setStatusFilter={setStatusFilter} setPage={setPage} onSearch={loadUsers} />
        <ReconcilePanel reconcileData={reconcileData} reconcileLoading={reconcileLoading} showReconcile={showReconcile} onPreview={handleReconcilePreview} onApply={handleReconcileApply} onClose={() => { setShowReconcile(false); setReconcileData(null); }} />

        {/* Users Table */}
        <Card className="bg-[#1A1A1A] border-[#222222] rounded-lg overflow-hidden">
          <CardHeader className="border-b border-[#222222] py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-[#999999] font-medium">{total} users found</CardTitle>
              <span className="hidden lg:inline text-[10px] uppercase tracking-wider text-[#666666] font-semibold">Signed Up</span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-[#666666]">Loading...</div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center text-[#666666]">No users found</div>
            ) : (
              <div className="divide-y divide-[#1A1A1A]">
                {users.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-4 px-4 py-3 hover:bg-[#151515] transition-colors" data-testid={`user-row-${u.user_id}`}>
                    <div className="shrink-0">
                      {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" /> : (
                        <div className="w-9 h-9 rounded-full bg-[#222222] flex items-center justify-center text-white text-sm font-semibold">{u.name?.[0] || '?'}</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openUserDetail(u.user_id)}>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-white truncate">{u.name}</p>
                        {u.role === 'admin' && <Crown className="h-3.5 w-3.5 text-[#F59E0B]" weight="fill" />}
                        {u.banned && <span className="text-[9px] font-bold text-red-400 bg-red-400/10 px-1.5 py-0.5 rounded">BANNED</span>}
                      </div>
                      <p className="text-xs text-[#666666] truncate">
                        {u.email} {u.username ? `  $${u.username}` : ''}
                        <span className="lg:hidden"> &middot; {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
                      </p>
                    </div>
                    <div className="hidden lg:block text-xs text-[#666666] whitespace-nowrap" data-testid={`signup-date-${u.user_id}`}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </div>
                    <div className="hidden md:flex items-center gap-4 text-xs text-[#666666]">
                      <span className="flex items-center gap-1"><CurrencyDollar className="h-3.5 w-3.5" />{formatCurrency(u.wallet_balance)}</span>
                      <span className="flex items-center gap-1"><ArrowsLeftRight className="h-3.5 w-3.5" />{u.transaction_count || 0}</span>
                      <span className="flex items-center gap-1"><Package className="h-3.5 w-3.5" />{u.product_count || 0}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => setBalanceDialog(u)} className="text-[#999999] hover:text-white hover:bg-[#333333] h-8 w-8 p-0" title="Adjust Balance" data-testid={`adjust-balance-${u.user_id}`}><CurrencyDollar className="h-4 w-4" /></Button>
                      {u.role !== 'admin' && <Button variant="ghost" size="sm" onClick={() => handleRoleChange(u.user_id, u.name, 'admin')} className="text-[#999999] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10 h-8 w-8 p-0" title="Make Admin" data-testid={`make-admin-${u.user_id}`}><Shield className="h-4 w-4" /></Button>}
                      {u.role === 'admin' && u.user_id !== user.user_id && <Button variant="ghost" size="sm" onClick={() => handleRoleChange(u.user_id, u.name, 'user')} className="text-[#999999] hover:text-[#999999] hover:bg-[#333333] h-8 w-8 p-0" title="Remove Admin" data-testid={`remove-admin-${u.user_id}`}><ShieldSlash className="h-4 w-4" /></Button>}
                      {u.user_id !== user.user_id && (u.banned ? (
                        <Button variant="ghost" size="sm" onClick={() => handleUnban(u.user_id, u.name)} className="text-[#22C55E] hover:bg-[#22C55E]/10 h-8 w-8 p-0" title="Unban" data-testid={`unban-${u.user_id}`}><Shield className="h-4 w-4" weight="fill" /></Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => handleBan(u.user_id, u.name)} className="text-[#999999] hover:text-red-400 hover:bg-red-400/10 h-8 w-8 p-0" title="Ban" data-testid={`ban-${u.user_id}`}><Prohibit className="h-4 w-4" /></Button>
                      ))}
                      {u.user_id !== user.user_id && u.role !== 'admin' && <Button variant="ghost" size="sm" onClick={() => handleDelete(u.user_id, u.name, u.email)} className="text-[#999999] hover:text-red-500 hover:bg-red-500/10 h-8 w-8 p-0" title="Delete User" data-testid={`delete-${u.user_id}`}><Trash className="h-4 w-4" /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#222222]">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-[#999999] hover:text-white"><CaretLeft className="h-4 w-4 mr-1" /> Previous</Button>
                <span className="text-xs text-[#666666]">Page {page} of {totalPages}</span>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-[#999999] hover:text-white">Next <CaretRight className="h-4 w-4 ml-1" /></Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Balance Adjustment Dialog */}
      <Dialog open={!!balanceDialog} onOpenChange={() => setBalanceDialog(null)}>
        <DialogContent className="bg-[#1A1A1A] border-[#333333] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Adjust Balance</DialogTitle>
            <DialogDescription className="text-[#999999]">{balanceDialog?.name} — Current: {formatCurrency(balanceDialog?.wallet_balance)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-[#999999]">Amount (positive to add, negative to deduct)</Label>
              <Input type="number" step="0.01" value={balanceAmount} onChange={(e) => setBalanceAmount(e.target.value)} placeholder="e.g., 50 or -25" className="bg-[#111111] border-[#333333] text-white mt-1" data-testid="balance-amount-input" />
            </div>
            <div>
              <Label className="text-xs text-[#999999]">Reason</Label>
              <Input value={balanceReason} onChange={(e) => setBalanceReason(e.target.value)} placeholder="Reason for adjustment" className="bg-[#111111] border-[#333333] text-white mt-1" data-testid="balance-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBalanceDialog(null)} className="border-[#333333] text-white">Cancel</Button>
            <Button onClick={handleBalanceAdjust} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-semibold" data-testid="confirm-balance-adjust">Adjust</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => { setDetailDialog(null); setDetailData(null); }}>
        <DialogContent className="bg-[#1A1A1A] border-[#333333] text-white max-w-lg max-h-[80vh] overflow-y-auto">
          {detailData && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {detailData.user.avatar_url ? <img src={detailData.user.avatar_url} alt="" className="w-10 h-10 rounded-full" /> : (
                    <div className="w-10 h-10 rounded-full bg-[#333333] flex items-center justify-center text-white font-bold">{detailData.user.name?.[0]}</div>
                  )}
                  <div>
                    <span className="text-white">{detailData.user.name}</span>
                    {detailData.user.username && <span className="text-xs text-[#666666] ml-2">${detailData.user.username}</span>}
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Balance', value: formatCurrency(detailData.user.wallet_balance) },
                    { label: 'Transactions', value: detailData.stats.transaction_count },
                    { label: 'Products', value: detailData.stats.product_count },
                    { label: 'Followers', value: detailData.stats.follower_count },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-[#111111] rounded-lg p-3">
                      <p className="text-[10px] uppercase text-[#666666] font-semibold">{label}</p>
                      <p className="text-sm font-bold text-white">{value}</p>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs text-[#666666] mb-1">Email: <span className="text-white">{detailData.user.email}</span></p>
                  <p className="text-xs text-[#666666]">Joined: <span className="text-white">{new Date(detailData.user.created_at).toLocaleDateString()}</span></p>
                </div>
                {detailData.recent_transactions.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-[#999999] mb-2">Recent Transactions</p>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {detailData.recent_transactions.slice(0, 5).map(t => (
                        <div key={t.transaction_id} className="flex justify-between text-xs py-1.5 px-2 rounded bg-[#111111]">
                          <span className="text-[#999999]">{t.type}</span>
                          <span className="text-white font-medium">{formatCurrency(t.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
